import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CreditCard, CheckCircle2, AlertTriangle, XCircle, Loader2, ExternalLink } from "lucide-react";

const STATUS_META = {
  enabled: { label: "Paiements activés", color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30", Icon: CheckCircle2 },
  requires_setup: { label: "Configuration requise", color: "text-[#E7C97A]", bg: "bg-[#C69C4A]/10 border-[#C69C4A]/30", Icon: AlertTriangle },
  pending: { label: "Configuration requise", color: "text-[#E7C97A]", bg: "bg-[#C69C4A]/10 border-[#C69C4A]/30", Icon: AlertTriangle },
  unavailable: { label: "Paiement indisponible", color: "text-red-400", bg: "bg-red-500/10 border-red-500/30", Icon: XCircle },
};

export default function ConnectPanel({ slug }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [notEnabled, setNotEnabled] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setStatus(await api.stripeStatus(slug)); }
    catch (e) { setStatus({ status: "unavailable" }); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  // refresh after returning from Stripe onboarding
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("stripe")) { load(); }
    // eslint-disable-next-line
  }, []);

  const onboard = async () => {
    setConnecting(true);
    setNotEnabled(false);
    try {
      const res = await api.stripeOnboard(slug, window.location.origin);
      window.location.href = res.onboarding_url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "connect_not_enabled") {
        setNotEnabled(true);
      } else {
        toast.error(typeof detail === "string" ? detail : "Erreur Stripe Connect");
      }
      setConnecting(false);
    }
  };

  const s = status?.status || "requires_setup";
  const meta = STATUS_META[s] || STATUS_META.requires_setup;

  return (
    <div data-testid="connect-panel" className="rounded-2xl bg-[#111F1A] border border-[#1B3028] p-5 space-y-4">
      <h3 className="font-serif-display text-lg flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#C69C4A]" /> Paiements en ligne (Stripe)</h3>
      <p className="text-sm text-stone-400">Chaque restaurant encaisse sur son propre compte Stripe. Les paiements de vos clients arrivent directement chez vous.</p>

      {loading ? (
        <div className="flex items-center gap-2 text-stone-400 text-sm py-4"><Loader2 className="w-4 h-4 animate-spin" /> Vérification du statut…</div>
      ) : (
        <div data-testid="connect-status" className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 ${meta.bg}`}>
          <meta.Icon className={`w-5 h-5 ${meta.color}`} />
          <span className={`font-semibold text-sm ${meta.color}`}>{meta.label}</span>
        </div>
      )}

      {s === "enabled" ? (
        <p className="text-xs text-emerald-400/80">✅ Votre compte est prêt. Les paiements par carte sont crédités sur votre compte Stripe.</p>
      ) : (
        <Button data-testid="connect-onboard-btn" onClick={onboard} disabled={connecting} className="rounded-full bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] font-semibold">
          {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
          {status?.connected ? "Continuer la configuration" : "Activer les paiements"}
        </Button>
      )}

      {notEnabled && (
        <div data-testid="connect-not-enabled" className="rounded-xl border border-[#C69C4A]/40 bg-[#12140C] p-4 text-sm text-stone-300 space-y-2">
          <p className="font-semibold text-[#E7C97A]">⚙️ Stripe Connect doit d'abord être activé sur la plateforme NOVANEXT</p>
          <p className="text-stone-400">L'activation des comptes connectés se fait une seule fois côté plateforme. Étapes :</p>
          <ol className="list-decimal list-inside text-stone-400 space-y-1">
            <li>Réclamez le compte Stripe NOVANEXT via le lien d'onboarding fourni.</li>
            <li>Dans le Dashboard Stripe → <b>Connect</b> → « Get started », renseignez le profil plateforme.</li>
            <li>Activez la fonctionnalité <b>Connect</b> (mode test), puis revenez ici et cliquez à nouveau sur « Activer les paiements ».</li>
          </ol>
          <p className="text-xs text-stone-500">En attendant, les paiements de test fonctionnent sur le compte plateforme unique — rien n'est bloqué.</p>
        </div>
      )}
    </div>
  );
}
